import { useLazyGetFilesQuery } from '@api/files.api.rtk';
import { useLazyGetTemplateQuery } from '@api/templates.api.rtk';
import useDisclosure from '@hooks/useDisclosure';
import { ModalWrapper } from '@hooks/useModal';
import useS3Bucket from '@hooks/useS3Bucket';
import useToast from '@hooks/useToast';
import useUser from '@hooks/useUser';
import { Box, CircularProgress, Stack } from '@mui/material';
import { pdf } from '@react-pdf/renderer';
import { useAppSelector } from '@store/index';
import {
  selectPropertyDetails,
  selectPropertyTaxInfo,
} from '@store/selector/property.selector';
import { dateTimeFormat } from '@utils/format';
import moment from 'moment';
import { PDFDocument as PDFLibDocument } from 'pdf-lib';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import PDFDocument, { measureTemplate } from './react-pdf/pdf-document';
import { TableRow, TemplateState } from './react-pdf/react-pdf.interface';
import {
  isTemplateState,
  populateTemplateFields,
} from './react-pdf/utils/react-pdf.helper';
export interface IFormPDFRef {
  handleShowPDF: ({
    inputs,
    template,
    hideEmptyContent,
  }: {
    inputs: IFormInput | IFormInput[];
    template: TemplateState;
    hideEmptyContent?: boolean;
  }) => Promise<void>;
  handleGeneratePDF: ({
    inputs,
    template,
    hideEmptyContent,
  }: {
    inputs: IFormInput | IFormInput[];
    template: TemplateState;
    hideEmptyContent?: boolean;
  }) => Promise<Blob | null>;
  getPropertyLogo: (id?: string) => Promise<{
    propertyLogo: string | null;
    error?: string | null;
  }>;
  getHeaderDetails: ({ moduleName }: { moduleName: string }) => {
    propertyName: string;
    propertyDetails: string;
    user: string;
    printDateTime: string;
    moduleVersion: string;
  };
  getTemplate: ({
    department,
    category,
    templateName,
  }: {
    department: string;
    category: string;
    templateName: string;
  }) => Promise<TemplateState | undefined>;
}

export interface IFormInput {
  [key: string]: string | TableRow[];
}

interface IFormPDFProps {
  isModal?: boolean;
  title?: string;
  modalOnClose?: () => Promise<void> | void;
}

export const mergePDFs = async (
  pdfBlobs: Blob[],
  title?: string,
): Promise<Blob> => {
  // Create new PDF
  const mergedPdf = await PDFLibDocument.create();

  // Optionally set PDF title metadata
  if (title) {
    mergedPdf.setTitle(title);
  }

  // Loop through input PDFs
  for (const blob of pdfBlobs) {
    const arrayBuffer = await blob.arrayBuffer();
    const pdf = await PDFLibDocument.load(arrayBuffer);

    // Copy all pages from each PDF
    const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  // Save merged PDF as Uint8Array
  const mergedPdfBytes = await mergedPdf.save();

  // ✅ Safely convert Uint8Array → Blob
  const mergedBlob = new Blob(
    [
      mergedPdfBytes.buffer.slice(
        mergedPdfBytes.byteOffset,
        mergedPdfBytes.byteOffset + mergedPdfBytes.byteLength,
      ) as ArrayBuffer,
    ],
    { type: 'application/pdf' },
  );

  return mergedBlob;
};
export const handleGeneratePDF = async ({
  inputs,
  template,
  hideEmptyContent,
}: {
  inputs: IFormInput | IFormInput[];
  template: TemplateState;
  hideEmptyContent?: boolean;
}): Promise<{ blob: Blob; url: string } | null> => {
  try {
    if (!isTemplateState(template)) {
      throw new Error('Invalid template format.');
    }

    const newTemplate = await measureTemplate(template);

    const blobs: Blob[] = [];

    if (Array.isArray(inputs)) {
      for (const input of inputs) {
        const populatedTemplate = await populateTemplateFields(
          structuredClone(newTemplate),
          input,
        );

        const blob = await pdf(
          <PDFDocument
            template={populatedTemplate}
            measureTemplate={false}
            hideEmptyContent={hideEmptyContent}
          />,
        ).toBlob();

        blobs.push(blob);
      }
    } else {
      const populatedTemplate = await populateTemplateFields(
        newTemplate,
        inputs,
      );

      const blob = await pdf(
        <PDFDocument
          template={populatedTemplate}
          measureTemplate={false}
          hideEmptyContent={hideEmptyContent}
        />,
      ).toBlob();

      blobs.push(blob);
    }

    const finalBlob = await mergePDFs(blobs);

    // Create URL
    const url = URL.createObjectURL(finalBlob);

    return { blob: finalBlob, url };
  } catch (error) {
    return null;
  }
};

const FormPDF = forwardRef<IFormPDFRef, IFormPDFProps>(
  ({ isModal = true, title, modalOnClose }, ref) => {
    // GENRPAR TIN
    // const busRegType = getGenparValue('bus_reg_type');
    // const busTin = getGenparValue('bus_tin');
    // GENRPAR TIN END

    const { toast } = useToast();
    const modalDisclosure = useDisclosure();
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);

    const [fetchTemplate] = useLazyGetTemplateQuery();
    const { getFile } = useS3Bucket();
    const [getFiles] = useLazyGetFilesQuery();
    const propertyDetails = useAppSelector(selectPropertyDetails);
    const propertyTaxInfo = useAppSelector(selectPropertyTaxInfo);
    const propertyID = useAppSelector(
      (state) => state.properties.selectedProperty.value,
    );
    const userData = useUser();

    const getTemplate = useCallback(
      async ({
        department,
        category,
        templateName,
      }: {
        department: string;
        category: string;
        templateName: string;
      }): Promise<TemplateState | undefined> => {
        try {
          const template = await fetchTemplate({
            department,
            category,
            templateName,
          }).unwrap();

          if (!template) {
            toast({ message: 'No Template Found', severity: 'warning' });
            return;
          }

          return template;
        } catch (error) {
          toast({
            message: error,
            severity: 'error',
          });
        }
      },
      [getFile, getFiles, propertyID],
    );

    const getPropertyLogo = useCallback(
      async (
        id?: string,
      ): Promise<{ propertyLogo: string | null; error?: string | null }> => {
        const targetId = id || propertyID;
        if (!targetId) {
          return { propertyLogo: null, error: 'No property ID provided' };
        }

        try {
          const files = await getFiles(targetId).unwrap();

          if (files?.length > 0) {
            const logoFile = files.find((f) => f.descr === 'Property Logo');
            if (logoFile?.fileID) {
              const fileUrl = await getFile(logoFile.fileID);
              return { propertyLogo: fileUrl };
            }
          }

          return { propertyLogo: null };
        } catch (err) {
          toast({
            message: err,
            severity: 'error',
          });

          return { propertyLogo: null, error: null };
        }
      },
      [getFile, getFiles, propertyID],
    );

    const getHeaderDetails = useCallback(
      ({ moduleName }: { moduleName: string }) => ({
        propertyName: propertyDetails.propertyName,
        propertyDetails:
          moduleName === 'Xenia FOS'
            ? [
                propertyDetails.propertyAddress,
                '\n' + propertyDetails.propertyTel,
                '\n' + propertyDetails.propertyEmail,
              ]
                .filter(Boolean)
                .join('')
            : [
                propertyDetails.propertyTel,
                propertyDetails.propertyEmail,
                propertyDetails.propertyAddress,
                '\n' + propertyTaxInfo.property?.tin,
              ]
                .filter(Boolean)
                .join(' | '),
        user: userData?.name?.toString() || '',
        printDateTime: moment().format(dateTimeFormat),
        moduleVersion: `${moduleName} ${APP_VERSION}S`,
      }),
      [propertyDetails, propertyTaxInfo, userData],
    );

    const handleShowPDF = async ({
      inputs,
      template,
      hideEmptyContent,
    }: {
      inputs: IFormInput | IFormInput[];
      template: TemplateState;
      hideEmptyContent?: boolean;
    }) => {
      modalDisclosure.onOpen();
      await handleGeneratePDF({
        inputs,
        template,
        hideEmptyContent,
      });
    };

    const handleGeneratePDF = async ({
      inputs,
      template,
      hideEmptyContent,
    }: {
      inputs: IFormInput | IFormInput[];
      template: TemplateState;
      hideEmptyContent?: boolean;
    }): Promise<Blob | null> => {
      try {
        if (!isTemplateState(template)) {
          toast({ severity: 'warning', message: 'Invalid template format.' });
          throw new Error('Invalid template format.');
        }

        const newTemplate = await measureTemplate(template);

        const blobs: Blob[] = [];

        if (Array.isArray(inputs)) {
          for (const input of inputs) {
            const populatedTemplate = await populateTemplateFields(
              structuredClone(newTemplate),
              input,
            );

            const blob = await pdf(
              <PDFDocument
                template={populatedTemplate}
                measureTemplate={false}
                hideEmptyContent={hideEmptyContent}
              />,
            ).toBlob();

            blobs.push(blob);
          }
        } else {
          const populatedTemplate = await populateTemplateFields(
            newTemplate,
            inputs,
          );

          const blob = await pdf(
            <PDFDocument
              template={populatedTemplate}
              measureTemplate={false}
              hideEmptyContent={hideEmptyContent}
            />,
          ).toBlob();

          blobs.push(blob);
        }

        const finalBlob = await mergePDFs(blobs);

        // Create URL
        const url = URL.createObjectURL(finalBlob);
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        setPdfUrl(url);

        return finalBlob;
      } catch (error) {
        toast({
          severity: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Something went wrong on PDF',
        });
        modalDisclosure.onClose();
        return null;
      }
    };

    const handleClose = async () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
      modalDisclosure.onClose();
      await modalOnClose?.();
    };

    // #region useImperativeHandle
    useImperativeHandle(ref, () => ({
      handleShowPDF,
      handleGeneratePDF,
      getPropertyLogo,
      getHeaderDetails,
      getTemplate,
    }));
    // #endregion

    return (
      <>
        {isModal ? (
          <ModalWrapper
            {...{
              modalDisclosure,
              withHeader: {
                title: title || '',
                onClose: handleClose,
              },
              boxStyle: {
                width: { xs: '100%', md: '950px' },
                height: '100%',
                '.with-header-main': { p: 0 },
              },
            }}
          >
            <Stack flex={1} sx={{ height: '100%', position: 'relative' }}>
              {pdfUrl ? (
                <iframe src={pdfUrl} width='100%' height='100%' />
              ) : (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
            </Stack>
          </ModalWrapper>
        ) : (
          <Stack flex={1} sx={{ height: '100%' }}>
            {pdfUrl ? (
              <iframe src={pdfUrl} width='100%' height='100%' />
            ) : (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Stack>
        )}
      </>
    );
  },
);

export default FormPDF;
